/**
 * CLS 监控器
 */
export class CLSMonitor {
  samplingRate: number;
  // 最少需要多少条 entry 才认为有意义（可配置）
  minEntries: number;
  // 最大记录条目数，防止内存过大
  maxEntries: number;

  isActive: boolean;
  startTimestamp: number;
  // 记录捕获到的 layout-shift 条目数量
  entriesCount: number;

  // 当前 session 累计值与首末时间（用于计算最大 session）
  sessionValue: number;
  sessionFirstTime: number;
  sessionLastTime: number;
  sessionEntryCount: number;

  // 当前记录到的最大 CLS 值和对应的条目数量
  metricValue: number;
  metricEntryCount: number;

  performanceObserver: PerformanceObserver | null;
  finishTimestamp: number;
  report: (metric: any) => void;

  constructor() {
    this.samplingRate = 1;
    this.minEntries = 1;
    this.maxEntries = 200;

    this.isActive = false;
    this.startTimestamp = 0;
    this.entriesCount = 0;

    this.sessionValue = 0;
    this.sessionFirstTime = 0;
    this.sessionLastTime = 0;
    this.sessionEntryCount = 0;

    this.metricValue = -1;
    this.metricEntryCount = 0;

    this.performanceObserver = null;
    this.finishTimestamp = 0;
  }

  start(report: (metric: any) => void) {
    if (this.isActive) return;
    this.report = report;
    this.isActive = true;
    this.startTimestamp = Date.now();
    this.entriesCount = 0;
    this.sessionValue = 0;
    this.sessionFirstTime = null;
    this.sessionLastTime = null;
    this.sessionEntryCount = 0;
    this.metricValue = -1;
    this.metricEntryCount = 0;
    this.finishTimestamp = 0;

    this.setupPerformanceObserver();
    hbLogger.debug('CLS 监控已启动');
  }

  setupPerformanceObserver() {
    if (!(window as any).PerformanceObserver) {
      this.destroy();
      return;
    }

    try {
      this.performanceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const e = entry;
          if (this.isValidEntry(e)) {
            hbLogger.debug('记录CLS条目:', e);
            this.recordEntry(e);
          }
        });
      });

      this.performanceObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (err) {
      hbLogger.warn(`CLS 采集，PerformanceObserver 启动失败: ${err && (err as Error).message}`);
      this.destroy();
    }
  }

  recordEntry(entry) {
    if (!this.isActive) return;

    // 增加计数
    this.entriesCount += 1;

    // session 合并规则（与官方 onCLS 一致）
    if (
      this.sessionValue &&
      entry.startTime - this.sessionLastTime < 1000 &&
      entry.startTime - this.sessionFirstTime < 5000
    ) {
      this.sessionValue += entry.value;
      this.sessionLastTime = entry.startTime;
      this.sessionEntryCount += 1;
    } else {
      this.sessionValue = entry.value;
      this.sessionFirstTime = entry.startTime;
      this.sessionLastTime = entry.startTime;
      this.sessionEntryCount = 1;
    }

    // 如果当前 session 超过历史最大值，则更新 metric（仅记录数值和数量）
    if (this.sessionValue > this.metricValue) {
      this.metricValue = this.sessionValue;
      this.metricEntryCount = this.sessionEntryCount;
    }

    // 如果到达上限则停止 observer 并记录完成时间
    if (this.entriesCount >= this.maxEntries) {
      this.stopObserver();
      this.finishTimestamp = Date.now();
    }
  }

  getMetrics() {
    if (!this.isActive || this.entriesCount < this.minEntries) return null;

    const finishTime = this.finishTimestamp || Date.now();
    const value = this.metricValue < 0 ? 0 : Number(this.metricValue.toFixed(6));

    return {
      type: 'ymc',
      name: 'CLS',
      value,
      count: this.entriesCount,
      metricEntryCount: this.metricEntryCount,
      startTime: this.startTimestamp,
      finishTime,
      duration: finishTime - this.startTimestamp,
    };
  }

  stopObserver() {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;
      this.reset(false);
    }
  }
  /**
   * 执行上报逻辑
   */
  reportMetrics() {
    const metric = this.getMetrics();
    if (metric && this.report) {
      this.report(metric);
      hbLogger.debug('CLS指标结果上报:', metric);
    }
  }
  reset(observeReset = true) {
    this.reportMetrics();
    this.entriesCount = 0;
    this.sessionValue = 0;
    this.sessionFirstTime = null;
    this.sessionLastTime = null;
    this.sessionEntryCount = 0;
    this.metricValue = -1;
    this.metricEntryCount = 0;
    this.startTimestamp = Date.now();
    this.finishTimestamp = 0;
    if (!this.performanceObserver && observeReset) this.setupPerformanceObserver();
    hbLogger.debug('CLS 监控已重置');
  }

  destroy() {
    this.isActive = false;
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;
    }
    hbLogger.debug('CLS 监控实例已销毁，资源已释放');
  }

  isValidEntry(entry) {
    if (!this.isActive || !this.shouldSample()) return false;
    // 必须没有最近的用户输入（与官方 CLS 规则一致）
    if (entry.hadRecentInput) return false;
    // value 必须是正数
    if (typeof entry.value !== 'number' || entry.value <= 0) return false;
    return true;
  }

  shouldSample() {
    return Math.random() <= this.samplingRate;
  }
}

export const clsMonitor = new CLSMonitor();
